import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toggleEnvironment } from '../../../utils/apiClient';
import { getAuthHeaders, getApiUrl, API_ENDPOINTS } from '../../../config/api.config';
import { useTrialContext } from '../../../contexts/TrialContext';
import { 
  Box, 
  Flex, 
  Container, 
  Button, 
  Text, 
  Heading,
  Table,
  Dialog,
  IconButton,
  Select,
  Badge,
  TextField,
  Checkbox,
  Card
} from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { CSVExportButton } from '../../../components/CSVExport';
import TopBar from '../../../components/ui/TopBar/TopBar';

// TypeScript interfaces
interface Question {
  _id: string;
  question: string;
  marks: number;
  lowMarkRange: string;
  mediumMarkRange?: string;
  highMarkRange: string;
  rubric: string;
  createdAt: string;
}

interface Class {
  _id: string;
  name: string;
}

interface User {
  userId: string;
  accountType: 'trial' | 'premium' | 'basic';
}

const ITEMS_PER_PAGE = 10;
const QUESTION_TRIAL_LIMIT = 3;

const ViewQuestions: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt-desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [classes, setClasses] = useState<Class[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [selectedQuestionText, setSelectedQuestionText] = useState<string>('');
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [addingToClass, setAddingToClass] = useState<boolean>(false);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const { checkLimit, trialStatus } = useTrialContext();
  
  // Get user account type from localStorage
  const userDataString = localStorage.getItem('user');
  const userAccount: User | null = userDataString ? JSON.parse(userDataString) : null;
  const isTrialAccount = userAccount?.accountType === 'trial';
  const isQuestionLimitReached = isTrialAccount && questions.length >= QUESTION_TRIAL_LIMIT;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('No user data found');
        }
        const user: User = JSON.parse(userData);
        const teacherId = user.userId;
        const response = await axios.get(
          `/api/v1/stem@home/question/questions/teacher/${teacherId}`,
          {
            baseURL: toggleEnvironment(),
            headers: getAuthHeaders()
          }
        );
        setQuestions(response.data.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError("Failed to load questions");
        setQuestions([]);
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    // Filter questions based on search term
    const filtered = questions.filter(question =>
      question.question.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredQuestions(filtered);
    setCurrentPage(1); // Reset to first page when searching
    setSelectedQuestions([]); // Clear selections when filtering
  }, [questions, searchTerm]);

  const fetchClasses = async (): Promise<void> => {
    try {
      const response = await axios.get(
        '/api/v1/stem@home/teacher/classes',
        {
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders()
        }
      );
      if (response.data && response.data.data) {
        setClasses(response.data.data);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    }
  };

  const handleAddToClass = async (): Promise<void> => {
    if (!selectedQuestionId || !selectedClassId) return;
    
    setAddingToClass(true);
    try {
      await axios.post(
        '/api/v1/stem@home/question/questions/add-to-class',
        {
          questionId: selectedQuestionId,
          classId: selectedClassId
        },
        {
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders()
        }
      );
      alert("Question added to class successfully!");
      setShowModal(false);
      setSelectedQuestionId(null);
      setSelectedQuestionText('');
      setSelectedClassId('');
    } catch (error) {
      console.error("Error adding question to class:", error);
      alert("Failed to add question to class. Please try again.");
    } finally {
      setAddingToClass(false);
    }
  };

  const handleDelete = async (questionId: string): Promise<void> => {
    const isConfirmed = window.confirm('Are you sure you want to delete this question?');
    
    if (isConfirmed) {
      try {
        const response = await axios.delete(
          `/api/v1/stem@home/question/questions/delete/${questionId}`,
          {
            baseURL: toggleEnvironment(),
            headers: getAuthHeaders()
          }
        );
        
        if (response.data && response.data.status === 'success') {
          setQuestions(questions.filter(q => q._id !== questionId));
          alert('Question deleted successfully!');
        } else {
          alert('Failed to delete question');
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        alert('Failed to delete question. Please try again.');
      }
    }
  };

  const handleEdit = (questionId: string): void => {
    navigate(`/edit-question/${questionId}`);
  };

  const openClassModal = (questionId: string, questionText: string): void => {
    setSelectedQuestionId(questionId);
    setSelectedQuestionText(questionText);
    setSelectedClassId('');
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setSelectedQuestionId(null);
    setSelectedQuestionText('');
    setSelectedClassId('');
  };

  const sortOptions = [
    { label: 'Date Added (Newest First)', value: 'createdAt-desc' },
    { label: 'Date Added (Oldest First)', value: 'createdAt-asc' },
    { label: 'Marks (High to Low)', value: 'marks-desc' },
    { label: 'Marks (Low to High)', value: 'marks-asc' }
  ];

  const handleSort = (value: string): void => {
    setSortBy(value);
  };

  const getSortedQuestions = (): Question[] => {
    const sortedQuestions = [...filteredQuestions];
    const [field, direction] = sortBy.split('-');

    return sortedQuestions.sort((a, b) => {
      if (field === 'createdAt') {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return direction === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      }
      
      if (field === 'marks') {
        const marksA = parseInt(a.marks.toString()) || 0;
        const marksB = parseInt(b.marks.toString()) || 0;
        return direction === 'asc' ? marksA - marksB : marksB - marksA;
      }

      return 0;
    });
  };

  const getPaginatedQuestions = (): Question[] => {
    const sortedQuestions = getSortedQuestions();
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedQuestions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const totalPages = Math.ceil(filteredQuestions.length / ITEMS_PER_PAGE);

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    setSelectedQuestions([]); // Clear selections when changing pages
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Selection handlers
  const handleSelectQuestion = (questionId: string, isChecked: boolean): void => {
    if (isChecked) {
      setSelectedQuestions(prev => [...prev, questionId]);
    } else {
      setSelectedQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSelectAll = (isChecked: boolean): void => {
    if (isChecked) {
      const allQuestionIds = getPaginatedQuestions().map(q => q._id);
      setSelectedQuestions(prev => {
        const newSelection = [...prev];
        allQuestionIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    } else {
      const currentPageIds = getPaginatedQuestions().map(q => q._id);
      setSelectedQuestions(prev => prev.filter(id => !currentPageIds.includes(id)));
    }
  };

  const isAllCurrentPageSelected = (): boolean => {
    const currentPageIds = getPaginatedQuestions().map(q => q._id);
    return currentPageIds.length > 0 && currentPageIds.every(id => selectedQuestions.includes(id));
  };

  if (loading) {
    return (
      <Container size="4" className="pt-8">
        <Flex align="center" justify="center" className="py-20 text-gray-500">
          <Text size="4">Loading questions...</Text>
        </Flex>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="4" className="pt-8">
        <Box className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <Heading size="6" className="text-gray-900">Questions</Heading>
        </Box>
        <Box className="bg-white rounded-lg p-12 shadow-sm text-center">
          <Text size="8" className="block mb-4">❌</Text>
          <Text size="5" className="block mb-6 text-gray-600">{error}</Text>
          <Button 
            size="3"
            variant="solid"
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => navigate('/insert-question')}
          >
            Add New Question
          </Button>
        </Box>
      </Container>
    );
  }

  if (questions.length === 0) {
    return (
      <Container size="4" className="pt-8">
        <Box className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <Heading size="6" className="text-gray-900">Questions</Heading>
        </Box>
        <Box className="bg-white rounded-lg p-12 shadow-sm text-center">
          <Text size="8" className="block mb-4">📝</Text>
          <Text size="5" className="block mb-6 text-gray-600">You haven't created any questions yet</Text>
          <Button 
            size="3"
            variant="solid"
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => navigate('/insert-question')}
          >
            Add New Question
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <TopBar 
        title={`Questions (${questions.length})`}
        showActions={true} 
      />
      <Box className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '80px' }}>
        {isQuestionLimitReached && (
          <Box className="bg-orange-100 border border-orange-300 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <Flex align="center" justify="center" gap="2">
              <Badge color="orange" size="2" className="md:size-3">
                ⚠️ FREE PLAN LIMIT REACHED
              </Badge>
            </Flex>
          </Box>
        )}
      
      {/* Mobile Controls */}
      <div className="block md:hidden mb-4 space-y-4">
        {/* Search Bar - Full Width */}
        <Box className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <TextField.Root
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
            size="3"
          />
        </Box>
        
        {/* Sort and Select All Row */}
        <Flex align="center" justify="between" gap="3">
          <Flex align="center" gap="2" className="flex-1">
            <Text size="2" className="text-gray-600 whitespace-nowrap">Sort:</Text>
            <Select.Root value={sortBy} onValueChange={handleSort}>
              <Select.Trigger className="flex-1 min-w-0" />
              <Select.Content>
                {sortOptions.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
          
          <Flex align="center" gap="2">
            <Checkbox
              checked={isAllCurrentPageSelected()}
              onCheckedChange={handleSelectAll}
            />
            <Text size="2" className="text-gray-600">All</Text>
          </Flex>
        </Flex>
        
                 {/* Export and Add Buttons Row */}
         <Flex align="center" gap="2">
           <CSVExportButton
             questions={questions}
             selectedQuestions={selectedQuestions}
             variant="all"
             className="flex-1"
           />
           <CSVExportButton
             questions={questions}
             selectedQuestions={selectedQuestions}
             variant="selected"
             className="flex-1"
           />
         </Flex>
        
        <Button 
          size="3"
          variant="solid"
          className="bg-black text-white hover:bg-gray-800 w-full"
          onClick={() => navigate('/insert-question')}
          disabled={isQuestionLimitReached}
          style={isQuestionLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          + Add New Question
        </Button>
      </div>
      
      {/* Desktop Controls */}
      <Flex justify="between" align="center" gap="4" className="mb-4 md:mb-6 hidden md:flex">
        <Flex align="center" gap="4" className="flex-1">
          <Box className="relative max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <TextField.Root
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              size="3"
            />
          </Box>
          
          <Flex align="center" gap="3">
            <Text size="3" className="text-gray-600">Sort by:</Text>
            <Select.Root value={sortBy} onValueChange={handleSort}>
              <Select.Trigger className="min-w-[200px]" />
              <Select.Content>
                {sortOptions.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>
        
        <Flex align="center" gap="3">
          <CSVExportButton
            questions={questions}
            selectedQuestions={selectedQuestions}
            variant="all"
            className="mr-2"
          />
          <CSVExportButton
            questions={questions}
            selectedQuestions={selectedQuestions}
            variant="selected"
            className="mr-2"
          />
          <Button 
            size="3"
            variant="solid"
            className="bg-black text-white hover:bg-gray-800 px-6"
            onClick={() => navigate('/insert-question')}
            disabled={isQuestionLimitReached}
            style={isQuestionLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            + Add New Question
          </Button>
        </Flex>
      </Flex>
      
      {/* Trial limit warning */}
      {isQuestionLimitReached && (
        <Box className="bg-red-500 text-white p-3 md:p-4 mb-4 md:mb-6 rounded font-bold text-center">
          <Text size="3" className="md:size-4">
            ⚠️ <strong>FREE PLAN LIMIT REACHED:</strong> You cannot create more than {QUESTION_TRIAL_LIMIT} questions with your free account
          </Text>
        </Box>
      )}

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-4">
        {getPaginatedQuestions().map((question) => (
          <Card key={question._id} className="p-4">
            <Flex align="start" gap="3">
              <Checkbox
                checked={selectedQuestions.includes(question._id)}
                onCheckedChange={(checked) => handleSelectQuestion(question._id, checked as boolean)}
                className="mt-1 flex-shrink-0"
              />
              <Box className="flex-1 min-w-0">
                <Text size="3" weight="medium" className="block mb-2 text-gray-900">
                  {question.question}
                </Text>
                
                <Flex direction="column" gap="2" className="mb-3">
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">Marks:</Text>
                    <Text size="2" weight="medium">{question.marks}</Text>
                  </Flex>
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">Low Range:</Text>
                    <Text size="2">{question.lowMarkRange}</Text>
                  </Flex>
                  {question.mediumMarkRange && (
                    <Flex justify="between" align="center">
                      <Text size="2" color="gray">Medium Range:</Text>
                      <Text size="2">{question.mediumMarkRange}</Text>
                    </Flex>
                  )}
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">High Range:</Text>
                    <Text size="2">{question.highMarkRange}</Text>
                  </Flex>
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">Created:</Text>
                    <Text size="2">{formatDateTime(question.createdAt)}</Text>
                  </Flex>
                </Flex>
                
                <Flex direction="column" gap="2">
                  <Button
                    size="2"
                    className="bg-green-600 hover:bg-green-700 text-white w-full"
                    onClick={() => openClassModal(question._id, question.question)}
                    disabled={addingToClass}
                  >
                    Add To Class
                  </Button>
                  <Flex gap="2">
                    <Button
                      size="2"
                      className="text-black flex-1"
                      style={{ backgroundColor: '#ffc107' }}
                      onClick={() => handleEdit(question._id)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="2"
                      className="bg-red-600 hover:bg-red-700 text-white flex-1"
                      onClick={() => handleDelete(question._id)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Flex>
          </Card>
        ))}
      </div>

      {/* Desktop Table Layout */}
      <Box className="bg-white rounded-lg border border-gray-200 overflow-hidden w-full hidden md:block">
        <Table.Root>
          <Table.Header>
            <Table.Row className="bg-gray-50">
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-16">
                <Checkbox
                  checked={isAllCurrentPageSelected()}
                  onCheckedChange={handleSelectAll}
                />
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 w-auto">
                Question
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-24">
                Marks
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-32">
                Low Mark Range
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-32">
                Medium Mark Range
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-32">
                High Mark Range
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-40">
                Created
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="text-gray-700 font-semibold py-4 px-6 text-center w-80">
                Actions
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {getPaginatedQuestions().map((question) => (
              <Table.Row key={question._id} className="border-b border-gray-100 hover:bg-gray-50">
                <Table.Cell className="py-4 px-6 text-center">
                  <Checkbox
                    checked={selectedQuestions.includes(question._id)}
                    onCheckedChange={(checked) => handleSelectQuestion(question._id, checked as boolean)}
                  />
                </Table.Cell>
                <Table.Cell className="py-4 px-6">
                  <Text size="3" className="text-gray-900">
                    {question.question}
                  </Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6 text-center">
                  <Text size="3" className="text-gray-600">{question.marks}</Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6 text-center">
                  <Text size="3" className="text-gray-600">{question.lowMarkRange}</Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6 text-center">
                  <Text size="3" className="text-gray-600">
                    {question.mediumMarkRange || '-'}
                  </Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6 text-center">
                  <Text size="3" className="text-gray-600">{question.highMarkRange}</Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6 text-center">
                  <Text size="3" className="text-gray-600">{formatDateTime(question.createdAt)}</Text>
                </Table.Cell>
                <Table.Cell className="py-4 px-6">
                  <Flex gap="3" justify="center">
                    <Button
                      size="2"
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm min-w-[100px]"
                      onClick={() => openClassModal(question._id, question.question)}
                      disabled={addingToClass}
                    >
                      Add To Class
                    </Button>
                    <Button
                      size="2"
                      className="text-black px-4 py-2 text-sm min-w-[60px]"
                      style={{ backgroundColor: '#ffc107' }}
                      onClick={() => handleEdit(question._id)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="2"
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm min-w-[60px]"
                      onClick={() => handleDelete(question._id)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex align="center" justify="center" gap="4" className="mt-6 md:mt-8">
          <Button 
            size="2"
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={currentPage === 1 ? 'invisible' : ''}
          >
            Previous
          </Button>
          <Flex gap="2" className="hidden sm:flex">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <Button
                key={page}
                size="2"
                variant={currentPage === page ? "solid" : "outline"}
                onClick={() => handlePageChange(page)}
                className={`min-w-[40px] ${
                  currentPage === page 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'hover:bg-gray-50'
                }`}
              >
                {page}
              </Button>
            ))}
          </Flex>
          <Text size="2" className="sm:hidden text-gray-600">
            Page {currentPage} of {totalPages}
          </Text>
          <Button 
            size="2"
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={currentPage === totalPages ? 'invisible' : ''}
          >
            Next
          </Button>
        </Flex>
      )}

      {/* Add Question to Class Modal */}
      {showModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 10px 38px -10px rgba(22, 23, 24, 0.35), 0 10px 20px -15px rgba(22, 23, 24, 0.2)',
              maxHeight: '85vh',
              overflow: 'auto',
              width: '90vw',
              maxWidth: '500px',
              zIndex: 1002
            }}
            onClick={(e) => e.stopPropagation()}
          >
          <Flex justify="between" align="center" className="mb-6">
            <Text size="5" weight="bold" className="text-xl font-semibold text-gray-900">
              Add Question to Class
            </Text>
            <IconButton
              variant="ghost"
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </IconButton>
          </Flex>
          
          <Box className="space-y-4">
            <Box>
              <Text size="3" weight="medium" className="block mb-2 text-gray-700">
                Question
              </Text>
              <Box className="p-3 bg-gray-50 rounded border text-gray-700">
                <Text size="3">{selectedQuestionText}</Text>
              </Box>
            </Box>
            
            <Box>
              <Text size="3" weight="medium" className="block mb-2 text-gray-700">
                Select Class
              </Text>
              <Select.Root value={selectedClassId} onValueChange={setSelectedClassId}>
                <Select.Trigger 
                  placeholder="Choose a class"
                  className="w-full"
                />
                <Select.Content>
                  {classes.map(cls => (
                    <Select.Item key={cls._id} value={cls._id}>
                      {cls.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
          </Box>
          
          <Flex gap="3" justify="end" className="mt-6">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button 
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleAddToClass}
              disabled={!selectedClassId || addingToClass}
            >
              {addingToClass ? 'Adding...' : 'Add to Class'}
            </Button>
          </Flex>
          </div>
        </div>
      )}
      </Box>
    </>
  );
};

export default ViewQuestions; 